// ═══════════════════════════════════════════════
//   KONBINI WORDLE — daily word game
// ═══════════════════════════════════════════════
//
// Objective:   Guess the hidden 5-letter konbini word in 6 tries.
// Type:        puzzle
// Description: A new word every day. Type letters and press ENTER to submit.
//              Green = right letter, right spot. Yellow = right letter, wrong spot.
//              Gray = not in the word. Fewer guesses used = higher score.
// Tunables:    GAME_WORDLE in data.js (and WORDLE_WORDS pool).

const {S, canvas, ctx, claimGameReward, addCleanup, showGameOver, todayStr, SFX} = window;

// ── Layout (fits 364×320 canvas) ─────────────────────────────────────
const WD_LETTERS = 5;
const WD_GUESSES = 6;
const WD_TILE    = 32;
const WD_GAP     = 4;
const WD_GRID_Y  = 24;
const WD_KEY_W   = 21;
const WD_KEY_H   = 19;
const WD_KEY_GAP = 3;
const WD_KB_Y    = 257;   // top of on-screen keyboard (rows fit exactly to y=320)
const WD_ENT_W   = 32;
const WD_DEL_W   = 32;

// ── Game state ────────────────────────────────────────────────────────
let wdTarget   = '';
let wdGuesses  = [];    // [{word, result: ['correct'|'present'|'absent', ...]}]
let wdCurrent  = '';
let wdStatus   = 'playing';  // 'playing' | 'won' | 'lost'
let wdShake    = 0;
let wdKeyState = {};    // letter → 'correct' | 'present' | 'absent'

// ── Daily word (deterministic from today's date) ───────────────────────
function wdPickDaily() {
    const today = (typeof todayStr === 'function' ? todayStr() : new Date().toISOString().slice(0, 10));
    let h = 5381;
    for (const c of today) h = (Math.imul(h, 31) + c.charCodeAt(0)) | 0;
    const pool = window.WORDLE_WORDS;
    return pool[Math.abs(h) % pool.length];
}

// ── Guess evaluation ───────────────────────────────────────────────────
function wdEvaluate(guess, target) {
    const result = Array(WD_LETTERS).fill('absent');
    const left   = target.split('');
    for (let i = 0; i < WD_LETTERS; i++) {
        if (guess[i] === target[i]) { result[i] = 'correct'; left[i] = null; }
    }
    for (let i = 0; i < WD_LETTERS; i++) {
        if (result[i] === 'correct') continue;
        const j = left.indexOf(guess[i]);
        if (j !== -1) { result[i] = 'present'; left[j] = null; }
    }
    return result;
}

// ── Input actions ──────────────────────────────────────────────────────
function wdType(letter) {
    if (wdStatus !== 'playing' || wdCurrent.length >= WD_LETTERS) return;
    wdCurrent += letter;
    SFX.play('click');
}

function wdDelete() {
    if (wdCurrent.length > 0) wdCurrent = wdCurrent.slice(0, -1);
}

function wdSubmit() {
    if (wdStatus !== 'playing') return;
    if (wdCurrent.length < WD_LETTERS) { wdShake = 10; SFX.play('error'); return; }

    const result = wdEvaluate(wdCurrent, wdTarget);
    wdGuesses.push({word: wdCurrent, result});

    for (let i = 0; i < WD_LETTERS; i++) {
        const ch = wdCurrent[i], r = result[i], cur = wdKeyState[ch];
        if (r === 'correct' || !cur || (r === 'present' && cur === 'absent')) wdKeyState[ch] = r;
    }

    if (wdCurrent === wdTarget) {
        wdStatus = 'won';
        window.gameScore = WD_GUESSES + 1 - wdGuesses.length; // 6→1
        document.getElementById('mg-score').textContent = window.gameScore;
        SFX.play('win');
        setTimeout(endUnscramble, 2000);
    } else if (wdGuesses.length >= WD_GUESSES) {
        wdStatus = 'lost';
        window.gameScore = 0;
        SFX.play('gameover');
        setTimeout(endUnscramble, 2500);
    } else {
        SFX.play('hit');
    }
    wdCurrent = '';
}

// ── Keyboard input ─────────────────────────────────────────────────────
function wdKeyDown(e) {
    if (!window.gameRunning || S.currentGame !== 'unscramble') return;
    if (wdStatus !== 'playing') return;
    if (e.key === 'Enter')     { e.preventDefault(); wdSubmit(); }
    else if (e.key === 'Backspace') { e.preventDefault(); wdDelete(); }
    else if (/^[A-Za-z]$/.test(e.key)) { e.preventDefault(); wdType(e.key.toUpperCase()); }
}

// ── Lifecycle ──────────────────────────────────────────────────────────
function startUnscramble() {
    wdTarget   = wdPickDaily();
    wdGuesses  = [];
    wdCurrent  = '';
    wdStatus   = 'playing';
    wdShake    = 0;
    wdKeyState = {};
    window.gameRunning = true;
    window.gameScore   = 0;
    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = '∞';

    window.gameInterval = setInterval(wdDraw, 50);
    addCleanup(() => clearInterval(window.gameInterval));

    document.addEventListener('keydown', wdKeyDown);
    addCleanup(() => document.removeEventListener('keydown', wdKeyDown));

    canvas.addEventListener('click', unsClick);
    addCleanup(() => canvas.removeEventListener('click', unsClick));

    addCleanup(() => { wdGuesses = []; wdCurrent = ''; wdKeyState = {}; });
}

function endUnscramble() {
    if (!window.gameRunning) return;
    window.gameRunning = false;
    const CFG = window.GAME_WORDLE;
    const p   = Math.min(CFG.rewardMax, window.gameScore * CFG.rewardPer);
    const msg = wdStatus === 'won'
        ? `Got it in ${wdGuesses.length}!\n`
        : `The word was: ${wdTarget}\n`;
    showGameOver(msg + claimGameReward('unscramble', window.gameScore, p));
}

// ── Canvas click → on-screen keyboard ────────────────────────────────
function unsClick(e) {
    if (!window.gameRunning || S.currentGame !== 'unscramble') return;
    if (wdStatus !== 'playing') return;
    const {x, y} = canvasPos(e);
    const W = canvas.width;
    const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];

    for (let row = 0; row < 3; row++) {
        const ky = WD_KB_Y + row * (WD_KEY_H + WD_KEY_GAP);
        if (y < ky || y >= ky + WD_KEY_H) continue;
        const keys = ROWS[row];
        if (row === 2) {
            const midW   = keys.length * (WD_KEY_W + WD_KEY_GAP) - WD_KEY_GAP;
            const totalW = WD_ENT_W + WD_KEY_GAP + midW + WD_KEY_GAP + WD_DEL_W;
            const sx     = (W - totalW) / 2;
            const midSx  = sx + WD_ENT_W + WD_KEY_GAP;
            const delSx  = midSx + midW + WD_KEY_GAP;
            if (x >= sx    && x < sx + WD_ENT_W)   { wdSubmit(); return; }
            if (x >= delSx && x < delSx + WD_DEL_W) { wdDelete(); return; }
            for (let i = 0; i < keys.length; i++) {
                const kx = midSx + i * (WD_KEY_W + WD_KEY_GAP);
                if (x >= kx && x < kx + WD_KEY_W) { wdType(keys[i]); return; }
            }
        } else {
            const totalW = keys.length * (WD_KEY_W + WD_KEY_GAP) - WD_KEY_GAP;
            const sx     = (W - totalW) / 2;
            for (let i = 0; i < keys.length; i++) {
                const kx = sx + i * (WD_KEY_W + WD_KEY_GAP);
                if (x >= kx && x < kx + WD_KEY_W) { wdType(keys[i]); return; }
            }
        }
        break;
    }
}

// ── Draw helpers ───────────────────────────────────────────────────────
function wdDrawKey(x, y, w, h, label, state) {
    const bg = state === 'correct' ? '#2a5a1a' :
               state === 'present' ? '#6e5500' :
               state === 'absent'  ? '#16161e' : '#26103a';
    const fg = state === 'absent'  ? '#555' :
               state === 'correct' ? '#b8ffb8' :
               state === 'present' ? '#ffe680' : '#ddd';
    const bd = state === 'correct' ? 'rgba(100,220,100,0.5)' :
               state === 'present' ? 'rgba(250,200,20,0.5)'  :
               state === 'absent'  ? 'rgba(255,255,255,0.07)' : 'rgba(200,80,180,0.35)';

    ctx.fillStyle = bg; ctx.strokeStyle = bd; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(x, y, w, h, 3); ctx.fill(); ctx.stroke();
    ctx.fillStyle = fg;
    ctx.font = `bold ${w <= WD_KEY_W ? 7 : 8}px "Press Start 2P", monospace`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, x + w / 2, y + h / 2 + 1);
}

// ── Main draw ─────────────────────────────────────────────────────────
function wdDraw() {
    if (!window.gameRunning) return;
    const W = canvas.width;

    // Background
    ctx.fillStyle = '#0e000e';
    ctx.fillRect(0, 0, W, canvas.height);

    // Title
    ctx.fillStyle = '#f9a8d4';
    ctx.font = 'bold 8px "Press Start 2P", monospace';
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('KONBINI WORDLE', W / 2, 17);

    // Shake offset (applied to the current row)
    let shakeX = 0;
    if (wdShake > 0) { shakeX = (wdShake % 2 === 0 ? 1 : -1) * 4; wdShake--; }

    // Grid
    const gridX = Math.floor((W - (WD_LETTERS * WD_TILE + (WD_LETTERS - 1) * WD_GAP)) / 2);

    for (let row = 0; row < WD_GUESSES; row++) {
        const guess  = row < wdGuesses.length ? wdGuesses[row] : null;
        const isCurr = row === wdGuesses.length && wdStatus === 'playing';
        const ox     = isCurr ? shakeX : 0;
        const ty     = WD_GRID_Y + row * (WD_TILE + WD_GAP);

        for (let col = 0; col < WD_LETTERS; col++) {
            const tx = gridX + col * (WD_TILE + WD_GAP);
            let bg = '#1a0028', border = 'rgba(220,60,160,0.25)', letter = '', letterCol = '#fff';

            if (guess) {
                const r = guess.result[col];
                bg        = r === 'correct' ? '#2a5a1a' : r === 'present' ? '#6e5500' : '#252532';
                border    = r === 'correct' ? '#6ade6a' : r === 'present' ? '#facc15' : '#3a3a4a';
                letter    = guess.word[col];
                letterCol = r === 'correct' ? '#b8ffb8' : r === 'present' ? '#ffe680' : '#bbb';
            } else if (isCurr && col < wdCurrent.length) {
                bg = '#280845'; border = 'rgba(255,255,255,0.65)'; letter = wdCurrent[col];
            } else if (isCurr) {
                border = 'rgba(220,60,160,0.55)';
            }

            ctx.fillStyle = bg; ctx.strokeStyle = border; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.roundRect(tx + ox, ty, WD_TILE, WD_TILE, 4); ctx.fill(); ctx.stroke();

            if (letter) {
                ctx.fillStyle = letterCol;
                ctx.font = 'bold 15px "Press Start 2P", monospace';
                ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
                ctx.fillText(letter, tx + ox + WD_TILE / 2, ty + WD_TILE / 2 + 1);
            }
        }
    }

    // Status message (in the gap between grid and keyboard)
    const statusY = WD_GRID_Y + WD_GUESSES * (WD_TILE + WD_GAP) + 14;
    if (wdStatus === 'won') {
        ctx.fillStyle = '#6ade6a';
        ctx.font = 'bold 8px "Press Start 2P", monospace';
        ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
        ctx.fillText(`${wdGuesses.length}/6 — nice!`, W / 2, statusY);
    } else if (wdStatus === 'lost') {
        ctx.fillStyle = '#f9a8d4';
        ctx.font = 'bold 8px "Press Start 2P", monospace';
        ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
        ctx.fillText(wdTarget, W / 2, statusY);
    }

    // On-screen keyboard
    const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
    for (let row = 0; row < 3; row++) {
        const keys = ROWS[row];
        const ky   = WD_KB_Y + row * (WD_KEY_H + WD_KEY_GAP);
        if (row === 2) {
            const midW   = keys.length * (WD_KEY_W + WD_KEY_GAP) - WD_KEY_GAP;
            const totalW = WD_ENT_W + WD_KEY_GAP + midW + WD_KEY_GAP + WD_DEL_W;
            const sx     = (W - totalW) / 2;
            const midSx  = sx + WD_ENT_W + WD_KEY_GAP;
            wdDrawKey(sx, ky, WD_ENT_W, WD_KEY_H, '↵', null);
            for (let i = 0; i < keys.length; i++)
                wdDrawKey(midSx + i * (WD_KEY_W + WD_KEY_GAP), ky, WD_KEY_W, WD_KEY_H, keys[i], wdKeyState[keys[i]]);
            wdDrawKey(midSx + midW + WD_KEY_GAP, ky, WD_DEL_W, WD_KEY_H, '⌫', null);
        } else {
            const totalW = keys.length * (WD_KEY_W + WD_KEY_GAP) - WD_KEY_GAP;
            const sx = (W - totalW) / 2;
            for (let i = 0; i < keys.length; i++)
                wdDrawKey(sx + i * (WD_KEY_W + WD_KEY_GAP), ky, WD_KEY_W, WD_KEY_H, keys[i], wdKeyState[keys[i]]);
        }
    }
}

// ── Expose ────────────────────────────────────────────────────────────
window.startUnscramble = startUnscramble;
window.endUnscramble   = endUnscramble;
window.unsClick        = unsClick;
window.wdDraw          = wdDraw;
